import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from '@ld.consumer-portal/utils';

function nativeBrowserEmailValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const tempInput = document.createElement('input');
  tempInput.type = 'email';
  tempInput.value = control.value;

  // Use the native input only to detect an invalid format; its validationMessage is untranslatable
  // browser UI text (locale follows the browser, not the app), so the template renders a Transloco
  // key instead of this message.
  if (!tempInput.validity.valid) {
    return { nativeEmailError: true };
  }

  return null;
}

export interface MigrationLoginFormData {
  email: string;
  password: string;
}

@Component({
  selector: 'ld-migration-login-card',
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslocoDirective,
  ],
  templateUrl: './migration-login-card.component.html',
  styleUrl: './migration-login-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MigrationLoginCardComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly themeService = inject(ThemeService);

  protected logoUrl = computed(() => this.themeService.getLogoUrl());
  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  initialEmail = input('');
  isEmailDisabled = input(false);
  isLoading = input(false);
  externalErrorMessage = input<string | null>(null);

  showBackButton = input(false);

  migrationLogin = output<MigrationLoginFormData>();
  passwordComplexityFailed = output<MigrationLoginFormData>();
  forgotPasswordClick = output<void>();
  backToLogin = output<void>();

  hidePassword = signal(true);
  loading = computed(() => this.isLoading());
  errorMessage = computed(() => this.externalErrorMessage());

  migrationForm = this.fb.group(
    {
      email: ['', [Validators.required, nativeBrowserEmailValidator]],
      password: ['', [Validators.required]],
    },
    { updateOn: 'blur' },
  );

  ngOnInit(): void {
    const email = this.initialEmail();
    if (email) {
      this.migrationForm.patchValue({ email });
    }

    if (this.isEmailDisabled()) {
      this.migrationForm.get('email')?.disable();
    }
  }

  togglePasswordVisibility(): void {
    this.hidePassword.update((hide) => !hide);
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.syncAutofillValues();
    this.migrationForm.markAllAsTouched();
    this.migrationForm.updateValueAndValidity();
    if (this.migrationForm.valid) {
      this.onSubmit();
    }
  }

  private syncAutofillValues(): void {
    const emailInput = document.getElementById('migration-email-input') as HTMLInputElement;
    if (emailInput?.value && emailInput.value !== this.migrationForm.get('email')?.value) {
      this.migrationForm.get('email')?.setValue(emailInput.value);
    }

    const passwordInput = document.getElementById('migration-password-input') as HTMLInputElement;
    if (passwordInput?.value && passwordInput.value !== this.migrationForm.get('password')?.value) {
      this.migrationForm.get('password')?.setValue(passwordInput.value);
    }
  }

  private meetsEntraPasswordComplexity(password: string): boolean {
    if (password.length < 8) return false;
    if (!/[a-zA-Z]/.test(password)) return false;
    if (!/[0-9]/.test(password)) return false;
    if (!/[^a-zA-Z0-9\s]/.test(password)) return false;
    return true;
  }

  onSubmit(): void {
    this.syncAutofillValues();

    if (this.migrationForm.invalid) {
      this.migrationForm.markAllAsTouched();
      return;
    }

    const formValue = this.migrationForm.getRawValue();
    const formData: MigrationLoginFormData = {
      email: formValue.email ?? '',
      password: formValue.password ?? '',
    };

    if (!this.meetsEntraPasswordComplexity(formData.password) || formData.password.toLowerCase() === formData.email.toLowerCase()) {
      this.passwordComplexityFailed.emit(formData);
      return;
    }

    this.migrationLogin.emit(formData);
  }

  onForgotPasswordClick(): void {
    this.forgotPasswordClick.emit();
  }

  onBackToLogin(): void {
    this.backToLogin.emit();
  }
}
